import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import ChartControls from "../../admin/components/ChartControls";
import { calculateDateRange, fillMissingPeriodRows, formatAxisTick, formatDate } from "../../admin/utils/chartHelpers";
import { useAffiliateAnalyticsTimeseries } from "../hooks/useAffiliateAnalytics";
import { affiliateBrandColors, affiliateSpinnerBorder } from "../utils/uiClasses";

const SERIES = [
  { key: "affiliated", label: "Affiliate Users", color: affiliateBrandColors.purple },
  { key: "paying", label: "Paying Users", color: affiliateBrandColors.pink },
];

const AnalyticsPage = () => {
  const defaultRange = calculateDateRange("last30days");
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [granularity, setGranularity] = useState("day");

  const timeseriesQuery = useAffiliateAnalyticsTimeseries({
    granularity,
    start_date: startDate,
    end_date: endDate,
    enabled: true,
  });

  const chartData = useMemo(() => {
    const rows = timeseriesQuery.data?.data || [];
    if (!rows.length) return [];
    return fillMissingPeriodRows(rows, granularity, startDate, endDate, {
      seriesKeys: SERIES.map((s) => s.key),
    });
  }, [timeseriesQuery.data?.data, granularity, startDate, endDate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-3 shadow-lg`}>
        <p className={`${typography.label} ${textColors.primary} mb-2`}>{formatDate(label, granularity)}</p>
        {payload.map((entry) => {
          const series = SERIES.find((s) => s.key === entry.dataKey);
          return (
            <p key={entry.dataKey} className={`${typography.bodySmall}`} style={{ color: entry.color }}>
              {series?.label || entry.dataKey}: {Number(entry.value || 0).toLocaleString()}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className={`${typography.h2} ${textColors.primary}`}>Analytics</h1>
            <p className={`${typography.body} ${textColors.secondary} mt-1`}>
              Affiliate signups and paid conversions over time.
            </p>
          </div>
          <Link
            to="/affiliate/dashboard/payments"
            className={`${typography.bodySmall} ${textColors.primary} underline hover:opacity-80`}
          >
            See My Payments
          </Link>
        </div>

        <ChartControls
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          granularity={granularity}
          onGranularityChange={setGranularity}
          showGranularitySelect={true}
        />

        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Affiliate Users & Paying Users</h3>

          {timeseriesQuery.isLoading && (
            <div className="flex items-center justify-center h-80">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${affiliateSpinnerBorder}`} />
            </div>
          )}

          {timeseriesQuery.error && (
            <div className="flex flex-col items-center justify-center h-80 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>Failed to load chart data</p>
              <p className={`${typography.bodySmall} ${textColors.muted}`}>
                {timeseriesQuery.error?.message || "Unknown error"}
              </p>
            </div>
          )}

          {!timeseriesQuery.isLoading && !timeseriesQuery.error && chartData.length === 0 && (
            <div className="flex flex-col items-center justify-center h-80 gap-3">
              <p className={`${typography.body} ${textColors.secondary}`}>
                No data available for the selected period
              </p>
              <p className={`${typography.caption} ${textColors.muted}`}>
                Share your coupon link and check back as signups and paid conversions come in.
              </p>
            </div>
          )}

          {!timeseriesQuery.isLoading && !timeseriesQuery.error && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className={`${textColors.muted} opacity-30`} />
                <XAxis
                  dataKey="period"
                  tickFormatter={(value) => formatDate(value, granularity)}
                  className={`${textColors.tertiary} opacity-80`}
                  stroke="currentColor"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  label={{ value: "Users", angle: -90, position: "insideLeft" }}
                  tickFormatter={formatAxisTick}
                  className={`${textColors.tertiary} opacity-80`}
                  stroke="currentColor"
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend formatter={(value) => SERIES.find((s) => s.key === value)?.label || value} />
                {SERIES.map((series) => (
                  <Bar
                    key={series.key}
                    dataKey={series.key}
                    name={series.key}
                    fill={series.color}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
